import React from "react";
import {
  Button,
  Card,
  Col,
  Form,
  Row,
  Badge,
  Spinner,
  InputGroup,
} from "@themesberg/react-bootstrap";
import { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { postApi } from "../../../redux/api";
import CustomTable from "../../../components/CustomTable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMoneyBillWave,
  faCheckCircle,
  faSearch,
  faSync,
  faCalendarAlt,
} from "@fortawesome/free-solid-svg-icons";
import { Routes } from "../../../routes";
import { formatNumber } from "../../../redux/utils";
import { useSelector } from "react-redux";
import moment from "moment";
import toast from "react-hot-toast";

function CashOfficePendingPayments() {
  const history = useNavigate();
  const user = useSelector((state) => state.auth.user);

  const [pvList, setPvList] = useState([]);
  const [selectedPvs, setSelectedPvs] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [filterText, setFilterText] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const fetchPendingPayments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await postApi("get_cash_office_payments", {
        form: { query_type: "pending" },
      });
      if (data?.result?.length) {
        setPvList(data.result);
      } else {
        setPvList([]);
      }
    } catch (err) {
      console.error("Error fetching pending payments:", err);
      setPvList([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingPayments();
  }, [fetchPendingPayments]);

  const toggleSelect = (pvCode) => {
    const next = new Set(selectedPvs);
    if (next.has(pvCode)) next.delete(pvCode);
    else next.add(pvCode);
    setSelectedPvs(next);
  };

  const selectAll = () => {
    if (selectedPvs.size === filteredList.length) {
      setSelectedPvs(new Set());
    } else {
      setSelectedPvs(new Set(filteredList.map((p) => p.pv_code)));
    }
  };

  const createBatch = async () => {
    if (selectedPvs.size === 0) return;
    setCreating(true);
    try {
      const data = await postApi("create_cash_office_batch", {
        pv_codes: Array.from(selectedPvs),
        userId: user?.username,
      });
      if (data?.success) {
        toast.success(
          `Batch ${data.batch_number} created successfully!`
        );
        history(
          `${Routes.cashOffice.review}?batch=${encodeURIComponent(data.batch_number)}`
        );
      } else {
        toast.error(data?.message || "Failed to create batch");
      }
    } catch (err) {
      toast.error("Failed to create batch. Please try again.");
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const filteredList = pvList.filter((item) => {
    if (filterText.trim()) {
      const search = filterText.toLowerCase();
      if (
        !item.pv_code?.toLowerCase().includes(search) &&
        !item.mda_name?.toLowerCase().includes(search) &&
        !item.contractor_name?.toLowerCase().includes(search)
      )
        return false;
    }
    if (dateFrom && moment(item.pv_date || item.date).isBefore(moment(dateFrom), "day"))
      return false;
    if (dateTo && moment(item.pv_date || item.date).isAfter(moment(dateTo), "day"))
      return false;
    return true;
  });

  const fields = [
    {
      title: (
        <Form.Check
          type="checkbox"
          onChange={selectAll}
          checked={selectedPvs.size === filteredList.length && filteredList.length > 0}
        />
      ),
      custom: true,
      component: (item) => (
        <Form.Check
          type="checkbox"
          checked={selectedPvs.has(item.pv_code)}
          onChange={() => toggleSelect(item.pv_code)}
        />
      ),
    },
    { title: "PV Code", value: "pv_code" },
    { title: "MDA Name", value: "mda_name" },
    { title: "Contractor", value: "contractor_name" },
    {
      title: "Narration",
      custom: true,
      component: (item) => (
        <span style={{ maxWidth: 200, display: "inline-block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {item.narration || item.description || "-"}
        </span>
      ),
    },
    {
      title: "Amount (₦)",
      tableHeadStyle: { textAlign: "right" },
      custom: true,
      component: (item) => (
        <div className="text-end">{formatNumber(item.amount)}</div>
      ),
    },
    {
      title: "Date",
      custom: true,
      component: (item) => (
        <span>{moment(item.pv_date || item.date).format("DD/MM/YYYY")}</span>
      ),
    },
  ];

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4">
        <div>
          <h4>
            <FontAwesomeIcon icon={faMoneyBillWave} className="me-2" />
            Pending Payments — Cash Office
          </h4>
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-primary" size="sm" onClick={fetchPendingPayments}>
            <FontAwesomeIcon icon={faSync} className="me-1" />
            Refresh
          </Button>
          <Button
            variant="success"
            size="sm"
            disabled={selectedPvs.size === 0 || creating}
            onClick={createBatch}
          >
            {creating ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FontAwesomeIcon icon={faCheckCircle} className="me-1" />
            )}
            Create Batch{selectedPvs.size > 0 ? ` (${selectedPvs.size})` : ""}
          </Button>
        </div>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <Row className="mb-3">
            <Col md={4}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faSearch} />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search by PV code, MDA, contractor..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </InputGroup.Text>
                <Form.Control
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  placeholder="From date"
                />
              </InputGroup>
            </Col>
            <Col md={3}>
              <InputGroup>
                <InputGroup.Text>
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </InputGroup.Text>
                <Form.Control
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  placeholder="To date"
                />
              </InputGroup>
            </Col>
            <Col md={2} className="d-flex align-items-center">
              <Badge bg="info" className="p-2">
                {selectedPvs.size} of {filteredList.length} selected
              </Badge>
            </Col>
          </Row>

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2">Loading pending payments...</p>
            </div>
          ) : filteredList.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FontAwesomeIcon icon={faMoneyBillWave} size="3x" className="mb-3" />
              <h5>No pending payments found</h5>
              <p>All approved PVs have been processed or none are eligible.</p>
            </div>
          ) : (
            <CustomTable
              fields={fields}
              data={filteredList}
              noSort={false}
              hover={true}
            />
          )}
        </Card.Body>
      </Card>
    </>
  );
}

export default CashOfficePendingPayments;
